// Financeiro › DRE & Resultado › "Setor dos custos": revisão, por lançamento do Contas a Pagar,
// do setor que o custo direto de contrato carrega na Margem por setor. Sugere, a pessoa confirma.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, App, Button, Card, Checkbox, Modal, Progress, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, RobotOutlined, CheckOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';
import SeletorSetores from '../../components/SeletorSetores';
import { recalcularFechamento, ErroDre } from '../administrativo-plano-acao/paginas/apiDre';
import { FiltroDePeriodo, usePeriodo } from '../administrativo-plano-acao/paginas/comumDre';
import { MAX_MESES_MARGEM, reais } from '../administrativo-plano-acao/paginas/dadosSetores';
import {
  mesmosSetores, nomesSetores, rotuloSetores, type FracoesSetores,
} from '../administrativo-plano-acao/paginas/dadosSetoresCusto';
import {
  agruparItens, aplicarSugestoesIa, competenciasAfetadas, corConfianca, origemDaEscolha, rotuloConfianca, rotuloSituacao,
  type ItemRevisao, type OrigemSetor,
} from './dadosSetoresCustoRevisao';

const { Text } = Typography;
const PERM_REVISAR = 'financeiro.setor_custo.revisar';
const MAX_IA = 60;
const MAX_GRAVAR = 500;

interface Resumo {
  n: number; valor: number;
  pendentes: { n: number; valor: number };
  pendentes_em_contrato_misto: { n: number; valor: number };
  sugestao_alta: { n: number; valor: number };
}
interface RespostaLista {
  de: string; ate: string; meses: string[]; resumo: Resumo; itens: ItemRevisao[];
  pessoas_sem_setor: { id: string; nome: string; valor: number }[];
  kits_sem_setor: { id: string; nome: string; valor: number }[];
}
interface ItemAlta {
  lancamento_id: string; fornecedor: string | null; linha: string; valor: number; contratos: string[];
  setores: FracoesSetores | null; origem: string; motivo?: string;
}
type Escolha = FracoesSetores | null;

/** Chamada à API com a mensagem de erro do servidor (400/403/502) em vez de um "erro genérico". */
async function chamar<T>(metodo: 'get' | 'post', caminho: string, params?: Record<string, any>, corpo?: any, timeout = 60000): Promise<T> {
  const token = await getToken();
  try {
    const r = await axios.request({ method: metodo, url: `${API_URL}${caminho}`, params, data: corpo, timeout, headers: { Authorization: token } });
    let d: any = r.data;
    if (d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch { /* segue */ } }
    if (d?.erro) throw new ErroDre(String(d.erro), r.status);
    return d as T;
  } catch (e: any) {
    if (e instanceof ErroDre) throw e;
    const status: number | null = e?.response?.status ?? null;
    let d: any = e?.response?.data;
    if (d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch { /* segue */ } }
    const msg = d?.erro || d?.error || d?.message;
    if (msg) throw new ErroDre(String(msg), status);
    if (status === 403) throw new ErroDre('Você não tem permissão para revisar o setor dos custos.', 403);
    if (status == null) throw new ErroDre('Sem resposta do servidor. Tente de novo em instantes.', null);
    throw new ErroDre(`Erro ${status}.`, status);
  }
}

const mesBr = (p: string) => (/^\d{4}\/\d{2}$/.test(p) ? `${p.slice(5)}/${p.slice(0, 4)}` : p);
const dataBr = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};
const curto = (s: string, n = 60) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '');
const num = (v: any) => Number(v) || 0;
const corValor = (v: number) => (v < 0 ? '#a8071a' : undefined);

/** O que o seletor mostra antes de a pessoa mexer: o gravado, senão a sugestão, senão o que a pessoa do lançamento já tem. */
function escolhaPadrao(it: ItemRevisao): Escolha {
  if (it.atual && !it.atual_ignorado) return it.atual.setores || null;
  if (it.situacao === 'pela_regra') return null;
  if (it.sugestao) return it.sugestao.setores || null;
  if (it.situacao === 'pela_pessoa' && it.pessoa?.setores) return it.pessoa.setores;
  return null;
}

type Linha =
  | { tipo: 'grupo'; key: string; chave: string; fornecedor: string; linha: string; n: number; valor: number; itens: ItemRevisao[] }
  | { tipo: 'item'; key: string; item: ItemRevisao };

export default function SetoresDosCustos({ hoje: hojeProp }: { hoje?: string }) {
  const { pode, carregando: carregandoPerms } = usePerms();
  const { message } = App.useApp();
  const podeRevisar = pode(PERM_REVISAR);
  const { hoje, filtro, setFiltro, janela } = usePeriodo(hojeProp, MAX_MESES_MARGEM);
  const de = janela.ok ? janela.de : null;
  const ate = janela.ok ? janela.ate : null;

  const [linhaF, setLinhaF] = useState<string | null>(null);
  const [contratoF, setContratoF] = useState<string | null>(null);
  const [soPendentes, setSoPendentes] = useState(true);
  const [soMuda, setSoMuda] = useState(true);

  const [dados, setDados] = useState<RespostaLista | null>(null);
  const [itens, setItens] = useState<ItemRevisao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [escolhas, setEscolhas] = useState<Record<string, Escolha>>({});
  const [escolhasGrupo, setEscolhasGrupo] = useState<Record<string, Escolha>>({});
  const [gravando, setGravando] = useState(false);
  const [iaOcupada, setIaOcupada] = useState(false);
  const [afetadas, setAfetadas] = useState<string[]>([]);
  const [recalc, setRecalc] = useState<{ feitos: number; total: number } | null>(null);
  // Opções dos filtros acumulam: filtrar por uma linha não pode esvaziar a lista para trocar de linha.
  const [opcLinhas, setOpcLinhas] = useState<string[]>([]);
  const [opcContratos, setOpcContratos] = useState<Record<string, string>>({});
  const [modalAltas, setModalAltas] = useState<{ itens: ItemAlta[]; n: number; valor: number } | null>(null);
  const [aplicandoAltas, setAplicandoAltas] = useState(false);

  const carregar = useCallback(async () => {
    if (!de || !ate) return;
    setCarregando(true); setErro(null);
    try {
      const params: Record<string, any> = { de, ate };
      if (linhaF) params.linha = linhaF;
      if (contratoF) params.contrato = contratoF;
      if (soPendentes) params.so_pendentes = 1;
      if (soMuda) params.so_muda = 1;
      const r = await chamar<RespostaLista>('get', '/financeiro/setores-custo', params);
      setDados(r); setItens(r.itens || []); setEscolhas({}); setEscolhasGrupo({});
      setOpcLinhas((ant) => Array.from(new Set(ant.concat((r.itens || []).map((i) => i.linha).filter(Boolean)))).sort());
      setOpcContratos((ant) => {
        const novo = { ...ant };
        (r.itens || []).forEach((i) => i.contratos.forEach((c) => { novo[c.id] = `${c.numero} — ${curto(c.titulo || '', 40)}`; }));
        return novo;
      });
    } catch (e: any) {
      setErro(e.message || 'Não consegui carregar.'); setDados(null); setItens([]);
    } finally { setCarregando(false); }
  }, [de, ate, linhaF, contratoF, soPendentes, soMuda]);

  useEffect(() => { carregar(); }, [carregar]);

  const escolhaDe = useCallback((it: ItemRevisao): Escolha => (
    Object.prototype.hasOwnProperty.call(escolhas, it.lancamento_id) ? escolhas[it.lancamento_id] : escolhaPadrao(it)
  ), [escolhas]);

  /** Grava e recarrega; mostra n e competências, ou o erro do servidor. */
  const gravar = async (lote: { item: ItemRevisao; escolha: Escolha }[]) => {
    if (!lote.length) return;
    if (lote.length > MAX_GRAVAR) { message.error(`No máximo ${MAX_GRAVAR} por vez.`); return; }
    const corpo = {
      itens: lote.map(({ item, escolha }) => {
        const vazia = !escolha || !Object.keys(escolha).length;
        const origem: OrigemSetor = origemDaEscolha(escolha, item.sugestao);
        return { lancamento_id: item.lancamento_id, setores: vazia ? null : escolha, origem };
      }),
    };
    setGravando(true);
    try {
      const r = await chamar<{ n: number; competencias_afetadas: string[] }>('post', '/financeiro/setores-custo/confirmar', undefined, corpo);
      setAfetadas((a) => competenciasAfetadas(a, r.competencias_afetadas));
      message.success(`${r.n} lançamento(s) gravado(s). Meses afetados: ${(r.competencias_afetadas || []).map(mesBr).join(', ') || 'nenhum'}.`);
      await carregar();
    } catch (e: any) {
      message.error(e.message || 'Não consegui gravar.');
    } finally { setGravando(false); }
  };

  const confirmarLinha = (it: ItemRevisao) => gravar([{ item: it, escolha: escolhaDe(it) }]);
  const segueContrato = (it: ItemRevisao) => gravar([{ item: it, escolha: null }]);
  const confirmarGrupo = (g: Extract<Linha, { tipo: 'grupo' }>) => {
    const escolha = Object.prototype.hasOwnProperty.call(escolhasGrupo, g.chave) ? escolhasGrupo[g.chave] : escolhaDe(g.itens[0]);
    // "pela regra" (despesa com CC) não entra no grupo: um setor específico se grava linha a linha
    const alvo = g.itens.filter((item) => item.situacao !== 'pela_regra');
    if (!alvo.length) { message.info('Este grupo é todo "pela regra" (despesa com centro de custo). Para dar um setor específico, confirme linha a linha.'); return; }
    gravar(alvo.map((item) => ({ item, escolha })));
  };
  const mudarGrupo = (g: Extract<Linha, { tipo: 'grupo' }>, nova: Escolha) => {
    setEscolhasGrupo((a) => ({ ...a, [g.chave]: nova }));
    setEscolhas((a) => { const n = { ...a }; g.itens.forEach((i) => { n[i.lancamento_id] = nova; }); return n; });
  };

  const abrirAltas = async () => {
    if (!de || !ate) return;
    setAplicandoAltas(true);
    try {
      const r = await chamar<{ n: number; valor: number; itens: ItemAlta[] }>('post', '/financeiro/setores-custo/confirmar-altas', undefined, { de, ate, dry_run: true });
      if (!r.n) { message.info('Nenhuma sugestão de confiança alta para gravar neste período.'); return; }
      setModalAltas({ itens: r.itens || [], n: r.n, valor: num(r.valor) });
    } catch (e: any) { message.error(e.message || 'Não consegui simular.'); }
    finally { setAplicandoAltas(false); }
  };
  const aplicarAltas = async () => {
    if (!de || !ate) return;
    setAplicandoAltas(true);
    try {
      // grava SÓ a lista que a pessoa viu no ensaio (ids); o servidor ainda pula o que alguém revisou no meio
      const ids = (modalAltas?.itens || []).map((i) => i.lancamento_id);
      const r = await chamar<{ n: number; competencias_afetadas: string[]; pulados?: { ja_revisados: number; sumidos: number; iguais: number } }>(
        'post', '/financeiro/setores-custo/confirmar-altas', undefined, { de, ate, dry_run: false, ...(ids.length ? { ids } : {}) });
      setAfetadas((a) => competenciasAfetadas(a, r.competencias_afetadas));
      const pulados = r.pulados ? r.pulados.ja_revisados + r.pulados.sumidos + r.pulados.iguais : 0;
      message.success(`${r.n} lançamento(s) gravado(s)${pulados ? ` (${pulados} pulado(s): já revisados ou apagados)` : ''}. Meses afetados: ${(r.competencias_afetadas || []).map(mesBr).join(', ') || 'nenhum'}.`);
      setModalAltas(null);
      await carregar();
    } catch (e: any) { message.error(e.message || 'Não consegui gravar.'); }
    finally { setAplicandoAltas(false); }
  };

  const sugerirIa = async () => {
    const alvo = itens.filter((i) => i.situacao === 'pendente').slice(0, MAX_IA).map((i) => i.lancamento_id);
    if (!alvo.length) { message.info('Não há pendentes na tela para a IA sugerir.'); return; }
    setIaOcupada(true);
    try {
      const r = await chamar<{ sugestoes: Record<string, any>; sem_resposta: string[] }>('post', '/financeiro/setores-custo/sugerir-ia', undefined, { ids: alvo }, 120000);
      setItens((atual) => aplicarSugestoesIa(atual, r.sugestoes));
      // Escolha já feita à mão não é pisada; as demais passam a mostrar a sugestão nova.
      setEscolhas((a) => { const n = { ...a }; Object.keys(r.sugestoes || {}).forEach((id) => { delete n[id]; }); return n; });
      const recebidas = Object.keys(r.sugestoes || {}).length;
      message.success(`IA sugeriu ${recebidas} de ${alvo.length}${alvo.length === MAX_IA ? ` (limite de ${MAX_IA} por vez)` : ''}. Nada foi gravado: confira e confirme.`);
    } catch (e: any) {
      message.error(e.status === 502 || e.message?.toLowerCase().includes('ia') ? `IA indisponível: ${e.message}` : (e.message || 'IA indisponível.'));
    } finally { setIaOcupada(false); }
  };

  const recalcular = async () => {
    const lista = afetadas.slice();
    if (!lista.length) return;
    const feitas: string[] = [];
    setRecalc({ feitos: 0, total: lista.length });
    try {
      for (const c of lista) {
        await recalcularFechamento(c);
        feitas.push(c);
        setRecalc({ feitos: feitas.length, total: lista.length });
      }
      message.success(`${lista.length} mês(es) recalculado(s). A Margem por setor já mostra o novo setor.`);
      setAfetadas([]);
    } catch (e: any) {
      setAfetadas((a) => a.filter((c) => feitas.indexOf(c) < 0));
      message.error(`Parou em ${mesBr(lista[feitas.length])}: ${e.message || 'erro ao recalcular'}. Os já feitos ficaram.`);
    } finally { setRecalc(null); }
  };

  const grupos = useMemo(() => agruparItens(itens), [itens]);
  const linhasTabela: Linha[] = useMemo(() => {
    const out: Linha[] = [];
    grupos.forEach((g) => {
      out.push({ tipo: 'grupo', key: `g-${g.chave}`, chave: g.chave, fornecedor: g.fornecedor, linha: g.linha, n: g.n, valor: g.valor, itens: g.itens });
      g.itens.forEach((item) => out.push({ tipo: 'item', key: item.lancamento_id, item }));
    });
    return out;
  }, [grupos]);

  const NCOLS = 9;
  const so = (r: Linha) => (r.tipo === 'item' ? r.item : null);

  const colunas: any[] = [
    {
      title: 'Mês', key: 'mes', width: 90,
      onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: NCOLS } : {}),
      render: (_: any, r: Linha) => {
        if (r.tipo === 'grupo') {
          const g = r;
          const eg = Object.prototype.hasOwnProperty.call(escolhasGrupo, g.chave) ? escolhasGrupo[g.chave] : escolhaDe(g.itens[0]);
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', background: '#fafafa', margin: '-8px -8px', padding: '8px' }}>
              <Text strong>{g.fornecedor}</Text>
              <Text type="secondary">{g.linha} · {g.n} lançamento(s) · </Text>
              <Text style={{ color: corValor(g.valor) }}>{reais(g.valor)}</Text>
              <div style={{ flex: '1 1 260px', maxWidth: 420, marginLeft: 'auto' }}>
                <SeletorSetores value={eg} onChange={(n) => mudarGrupo(g, n)} disabled={!podeRevisar}
                  placeholder="Setor do grupo (vazio = segue o contrato)" />
              </div>
              <Tooltip title="Grava o setor escolhido acima em todos os lançamentos do grupo">
                <Button size="small" type="primary" disabled={!podeRevisar || gravando} onClick={() => confirmarGrupo(g)}>Confirmar grupo</Button>
              </Tooltip>
            </div>
          );
        }
        const it = r.item;
        return (it.competencias && it.competencias.length > 1 ? it.competencias : [it.periodo]).map(mesBr).join(', ');
      },
    },
    {
      title: 'Fornecedor', key: 'forn', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        return (
          <div>
            <div>{it.fornecedor?.nome || '—'}</div>
            {it.pessoa ? <Text type="secondary" style={{ fontSize: 12 }}>Pessoa: {it.pessoa.nome}</Text> : null}
          </div>
        );
      },
    },
    {
      title: 'Descrição', key: 'desc', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => { const it = so(r); return it ? <Tooltip title={it.descricao}>{curto(it.descricao, 50)}</Tooltip> : null; },
    },
    {
      title: 'Linha', key: 'linha', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => so(r)?.linha || null,
    },
    {
      title: 'Contrato(s)', key: 'contratos', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        if (!it.contratos.length) return '—';
        return it.contratos.map((c) => (
          <div key={c.id}>
            <Tooltip title={c.titulo}>{c.numero}</Tooltip>{' '}
            <Text type="secondary" style={{ fontSize: 12 }}>{nomesSetores(c.mix)}</Text>
          </div>
        ));
      },
    },
    {
      title: 'Valor', key: 'valor', align: 'right', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => { const it = so(r); return it ? <span style={{ color: corValor(num(it.valor)) }}>{reais(num(it.valor))}</span> : null; },
    },
    {
      title: 'Situação', key: 'sit', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        return <Tag color={it.situacao === 'pendente' ? 'gold' : it.situacao === 'pela_pessoa' ? 'blue' : it.situacao === 'pela_regra' ? 'cyan' : 'green'}>{rotuloSituacao(it.situacao)}</Tag>;
      },
    },
    {
      title: 'Sugestão', key: 'sug', onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        const s = it.sugestao;
        return (
          <div>
            {s ? (
              <Tooltip title={`${rotuloConfianca(s.confianca)}${s.motivo ? ' — ' + s.motivo : ''}`}>
                <Tag color={corConfianca(s.confianca)} style={{ whiteSpace: 'normal' }}>
                  {s.setores ? rotuloSetores(s.setores) : 'Segue o contrato'}
                </Tag>
              </Tooltip>
            ) : it.situacao === 'pela_regra' ? (
              <Tooltip title={it.motivo_regra || 'Despesa com centro de custo do contrato: segue a divisão de receita dele. Para mudar, escolha um setor e confirme.'}>
                <Tag color="cyan" style={{ whiteSpace: 'normal' }}>Segue o contrato (regra)</Tag>
              </Tooltip>
            ) : <Text type="secondary">—</Text>}
            {s?.pessoa_sem_setor ? (
              <div style={{ fontSize: 12 }}>
                <Link to={`/administrativo/rh?pessoa=${encodeURIComponent(s.pessoa_sem_setor.id)}&aba=rateio`}>
                  Definir setor de {s.pessoa_sem_setor.nome}
                </Link>
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Setor', key: 'setor', width: 280, onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        return (
          <div style={{ minWidth: 220 }}>
            <SeletorSetores value={escolhaDe(it)} disabled={!podeRevisar}
              onChange={(n) => setEscolhas((a) => ({ ...a, [it.lancamento_id]: n }))} />
            {it.atual ? (
              <Text type={it.atual_ignorado ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                {it.atual_ignorado
                  ? `Marcação automática (${it.atual.origem}) ignorada: ${it.grupo === 'COMISSOES' ? 'comissão' : 'despesa com centro de custo'} só aceita marcação manual ou "segue o contrato"`
                  : it.atual.setores ? 'Gravado' : 'Segue o contrato'}{it.atual.por ? ` por ${it.atual.por}` : ''}{it.atual.em ? ` em ${dataBr(it.atual.em)}` : ''}
              </Text>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Ações', key: 'acoes', fixed: 'right', width: 150, onCell: (r: Linha) => (r.tipo === 'grupo' ? { colSpan: 0 } : {}),
      render: (_: any, r: Linha) => {
        const it = so(r); if (!it) return null;
        const esc = escolhaDe(it);
        // Nada a gravar quando o seletor já é o que está gravado (os dois vazios = segue o contrato).
        const igualAoGravado = !!it.atual && (it.atual.setores && esc ? mesmosSetores(esc, it.atual.setores) : !it.atual.setores && !esc);
        return (
          <Space size={4} wrap>
            <Button size="small" type="primary" icon={<CheckOutlined />} disabled={!podeRevisar || gravando || igualAoGravado}
              onClick={() => confirmarLinha(it)}>Confirmar</Button>
            <Button size="small" disabled={!podeRevisar || gravando} onClick={() => segueContrato(it)}>Segue o contrato</Button>
          </Space>
        );
      },
    },
  ];

  if (carregandoPerms) return <div style={{ padding: 40, textAlign: 'center' }}>Carregando…</div>;

  const resumo = dados?.resumo;
  const cartao = (titulo: string, v?: { n: number; valor: number }) => (
    <Card size="small" style={{ minWidth: 190, flex: '1 1 190px' }}>
      <Text type="secondary">{titulo}</Text>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{v ? v.n : '—'}</div>
      <Text type="secondary">{v ? reais(num(v.valor)) : ''}</Text>
    </Card>
  );
  const nPendentesVisiveis = itens.filter((i) => i.situacao === 'pendente').length;

  return (
    <div>
      {!podeRevisar ? (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Somente leitura"
          description="Você pode ver a lista, mas só quem tem a permissão de revisar o setor dos custos grava. Peça ao Lucas se precisar." />
      ) : null}

      <Space wrap size={[16, 8]} style={{ marginBottom: 12 }} align="center">
        <FiltroDePeriodo filtro={filtro} onChange={setFiltro} hoje={hoje} janela={janela} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Linha da DRE" style={{ width: 220 }}
          value={linhaF ?? undefined} onChange={(v) => setLinhaF(v ?? null)}
          options={opcLinhas.map((l) => ({ value: l, label: l }))} popupMatchSelectWidth={false} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="Contrato" style={{ width: 260 }}
          value={contratoF ?? undefined} onChange={(v) => setContratoF(v ?? null)}
          options={Object.keys(opcContratos).map((id) => ({ value: id, label: opcContratos[id] }))} popupMatchSelectWidth={false} />
        <Checkbox checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)}>Só pendentes</Checkbox>
        <Checkbox checked={soMuda} onChange={(e) => setSoMuda(e.target.checked)}>Só onde muda a margem</Checkbox>
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={carregando}>Atualizar</Button>
      </Space>

      {!janela.ok ? <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={janela.aviso} /> : null}
      {janela.ok && janela.aviso ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message={janela.aviso} /> : null}
      {erro ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={erro} /> : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        {cartao('Pendentes', resumo?.pendentes)}
        {cartao('Pendentes em contrato misto (a margem muda)', resumo?.pendentes_em_contrato_misto)}
        {cartao('Com sugestão de confiança alta', resumo?.sugestao_alta)}
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" disabled={!podeRevisar || aplicandoAltas || !janela.ok} loading={aplicandoAltas && !modalAltas}
          onClick={abrirAltas}>Confirmar todas as altas</Button>
        <Button icon={<RobotOutlined />} disabled={!podeRevisar || !nPendentesVisiveis} loading={iaOcupada} onClick={sugerirIa}>
          Sugerir com IA
        </Button>
        <Button disabled={!podeRevisar || !afetadas.length || !!recalc} loading={!!recalc} onClick={recalcular}>
          {`Recalcular ${afetadas.length} ${afetadas.length === 1 ? 'mês' : 'meses'}`}
        </Button>
        {recalc ? <Progress style={{ width: 160 }} size="small" percent={Math.round((recalc.feitos / recalc.total) * 100)} /> : null}
        {afetadas.length ? <Text type="secondary">A Margem por setor só muda depois de recalcular.</Text> : null}
      </Space>

      {dados && (dados.pessoas_sem_setor.length || dados.kits_sem_setor.length) ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          {dados.pessoas_sem_setor.length ? (
            <Card size="small" title={`Pessoas sem setor (${dados.pessoas_sem_setor.length})`} style={{ flex: '1 1 320px' }}>
              {dados.pessoas_sem_setor.slice(0, 8).map((p) => (
                <div key={p.id}>
                  <Link to={`/administrativo/rh?pessoa=${encodeURIComponent(p.id)}&aba=rateio`}>{p.nome}</Link>{' '}
                  <Text type="secondary">{reais(num(p.valor))}</Text>
                </div>
              ))}
              {dados.pessoas_sem_setor.length > 8 ? <Text type="secondary">e mais {dados.pessoas_sem_setor.length - 8}.</Text> : null}
            </Card>
          ) : null}
          {dados.kits_sem_setor.length ? (
            <Card size="small" title={`Kits sem setor (${dados.kits_sem_setor.length})`} style={{ flex: '1 1 320px' }}
              extra={<Link to="/operacional/kits">Abrir Kits</Link>}>
              {dados.kits_sem_setor.slice(0, 8).map((k) => (
                <div key={k.id}>{k.nome} <Text type="secondary">{reais(num(k.valor))}</Text></div>
              ))}
              {dados.kits_sem_setor.length > 8 ? <Text type="secondary">e mais {dados.kits_sem_setor.length - 8}.</Text> : null}
            </Card>
          ) : null}
        </div>
      ) : null}

      <Table<Linha>
        size="small" rowKey="key" loading={carregando} columns={colunas} dataSource={linhasTabela}
        scroll={{ x: 'max-content' }} pagination={{ pageSize: 50, showSizeChanger: false }}
        locale={{ emptyText: carregando ? 'Carregando…' : 'Nenhum lançamento neste filtro.' }}
      />

      <Modal
        open={!!modalAltas} title="Confirmar todas as altas" width="94vw" style={{ maxWidth: 1100, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
        onCancel={() => !aplicandoAltas && setModalAltas(null)}
        okText={modalAltas ? `Gravar ${modalAltas.n} lançamento(s)` : 'Gravar'} cancelText="Cancelar"
        okButtonProps={{ loading: aplicandoAltas }} onOk={aplicarAltas}
      >
        {modalAltas ? (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={`${modalAltas.n} lançamento(s), ${reais(modalAltas.valor)}. Só entram sugestões de confiança alta; a IA nunca entra aqui.`} />
            <Table<ItemAlta>
              size="small" rowKey="lancamento_id" dataSource={modalAltas.itens} scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 50, showSizeChanger: false }}
              columns={[
                { title: 'Fornecedor', dataIndex: 'fornecedor', render: (v) => v || '—' },
                { title: 'Linha', dataIndex: 'linha' },
                { title: 'Contrato(s)', dataIndex: 'contratos', render: (v: string[]) => (v || []).join(', ') },
                { title: 'Valor', dataIndex: 'valor', align: 'right', render: (v) => <span style={{ color: corValor(num(v)) }}>{reais(num(v))}</span> },
                { title: 'Setor', dataIndex: 'setores', render: (v) => (v ? rotuloSetores(v) : 'Segue o contrato') },
                { title: 'Origem', dataIndex: 'origem' },
                { title: 'Motivo', dataIndex: 'motivo' },
              ]}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
}
